// src/lib/hubData.js
import { db } from "@/firebase/config";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  withLabel,
} from "@/lib/firestore-instrumented";

export async function fetchActiveHubsForUser(uid, pageSize = 25, allowedHubSlugs = null) {
  if (!uid) return [];
  const memQ = withLabel(
    query(
      collection(db, "hubMembers"),
      where("uid", "==", uid),
      where("status", "==", "active"),
      limit(pageSize)
    ),
    `MyHubs:hubMembers:uid=${uid}(limit=${pageSize})`
  );
  const memSnap = await getDocs(memQ);
  if (memSnap.empty) return [];
  const hubIds = Array.from(
    new Set(
      memSnap.docs
        .map((d) => d.data()?.hubId)
        .filter(Boolean)
        .slice(0, pageSize)
    )
  );

  const hubs = await Promise.all(
    hubIds.map(async (hubId) => {
      try {
        const hubSnap = await getDoc(doc(db, "hubs", hubId));
        return hubSnap.exists() ? { id: hubSnap.id, ...hubSnap.data() } : null;
      } catch {
        return null;
      }
    })
  );
  const allowed = Array.isArray(allowedHubSlugs) ? new Set(allowedHubSlugs) : null;
  return hubs.filter((hub) => hub && (!allowed || allowed.has(hub.slug || hub.id)));
}

async function fetchVisibleHubs(pageSize, allowedHubSlugs, label) {
  if (Array.isArray(allowedHubSlugs)) {
    const hubs = await Promise.all(
      allowedHubSlugs.slice(0, pageSize).map(async (hubSlug) => {
        const snap = await getDoc(doc(db, "hubs", hubSlug));
        return snap.exists() ? { id: snap.id, ...snap.data() } : null;
      })
    );
    return hubs.filter(
      (hub) => hub && hub?.publicReadable !== false && hub?.type !== "private"
    );
  }
  const hubsQ = withLabel(
    query(
      collection(db, "hubs"),
      orderBy("slug", "asc"),
      limit(pageSize)
    ),
    label
  );
  const snap = await getDocs(hubsQ);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((hub) => hub?.publicReadable !== false && hub?.type !== "private");
}

export function fetchExploreHubs(pageSize = 25, allowedHubSlugs = null) {
  return fetchVisibleHubs(
    pageSize,
    allowedHubSlugs,
    `ExploreHubs:slug(limit=${pageSize})`
  );
}

export async function fetchPublicHubSlugs(pageSize = 50) {
  const hubs = await fetchVisibleHubs(
    pageSize,
    null,
    `Latest:publicHubs:slug(limit=${pageSize})`
  );
  return hubs.map((hub) => hub.slug || hub.id).filter(Boolean);
}
